import { createDiscreteApi } from 'naive-ui'
const { message, dialog, notification } = createDiscreteApi([
    'message',
    'dialog',
    'notification',
    'loadingBar',
  ])

type messageTypes = 'info' | 'success' | 'warning' | 'error'

type imageSize = {
  width: number
  height: number
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true
  }

  if (typeof value === 'string') {
    return value.trim().length === 0
  }

  if (Array.isArray(value)) {
    return value.length === 0
  }

  if (value instanceof Map || value instanceof Set || value instanceof File) {
    return value.size === 0
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0
  }

  return false
}

function showConfirm(msg: string, confirmCallBack: Function) {
  dialog.create({
    title: 'Warning',
    content: msg,
    positiveText: 'Confirm',
    negativeText: 'Cancel',
    onPositiveClick: () => {
      confirmCallBack()
    },
    onNegativeClick: () => {},
  })
}

function showAlert(msg: any, positive_func?: any) {
  dialog.warning({
    title: 'Tips',
    content: msg.errmsg || msg.message || msg,
    positiveText: 'I Got It',
    negativeText: '',
    onPositiveClick: () => {
      if (typeof positive_func == 'function') {
        positive_func()
      }
    },
    onNegativeClick: () => {},
  })
}

function showMessage(msg: any, msgtype: messageTypes) {
  let msg_str = msg.errmsg || msg.message || msg
  switch (msgtype) {
    case 'info':
      message.info(msg_str)
      break
    case 'success':
      message.success(msg_str)
      break
    case 'warning':
      message.warning(msg_str)
      break
    case 'error':
      message.error(msg_str)
      break
  }
}

function formatNumber(n: number) {
  const str = n.toString()
  return str[1] ? str : `0${str}`
}

/**
 * 格式化时间
 * @param ms -1: 当前时间戳
 * @param type 未定义: yyyy-mm-dd hh:MM:ss, 0: yyyymmddhhMMss, 1: yyyy-mm-dd, 2: 本地语言时间格式, 3: 月/日 时:分
 * @returns res
 */
function formatTime(ms: number, type?: number): string {
  let date
  if (ms == -1) {
    date = new Date()
  } else {
    date = new Date(ms)
  }
  let year = date.getFullYear(),
    month = date.getMonth() + 1,
    day = date.getDate(),
    hour = date.getHours(),
    min = date.getMinutes(),
    sec = date.getSeconds()

  month = formatNumber(month)
  day = formatNumber(day)
  hour = formatNumber(hour)
  min = formatNumber(min)
  sec = formatNumber(sec)

  let res: string
  if (typeof type == 'undefined') {
    res = `${year}-${month}-${day} ${hour}:${min}:${sec}`
  } else if (type == 0) {
    res = `${year}${month}${day}${hour}${min}${sec}`
  } else if (type == 1) {
    res = `${year}-${month}-${day}`
  } else if (type == 2) {
    // 配置选项：月份为全名，日期、年份、时间为数字
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false, // 24小时制
    }
    // 使用用户本地语言（如 'en-US'）格式化
    res = new Intl.DateTimeFormat('en-US', options).format(date)
  } else if (type == 3) {
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }
    res = new Intl.DateTimeFormat('en-US', options).format(date)
  }
  return res
}

function getImageSize(url: string) {
  return new Promise<imageSize>((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
      })
    }
    img.onerror = (err) => {
      reject(err)
    }
    img.src = url
  })
}

function download(item: any) {
  let url = item.image_url || item.photo_url || item.video_url || item,
    us = navigator.userAgent.toLowerCase(),
    is_mobile =
      /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(us)
  if (is_mobile) {
    window.location.href = url
    return
  }

  let img_type
  if (/^data:image\/[a-z]+;base64,/.test(url)) {
    img_type = getImageTypeFromBase64(url)
    url = URL.createObjectURL(base64ToBlob(url))
  }
  let a = document.createElement('a')
  a.style.display = 'none'
  a.target = '_blank'
  a.href = url
  if (img_type) {
    a.download = formatTime(-1, 0) + '.' + img_type
  }
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

function getImageTypeFromBase64(base64Data) {
  if (!base64Data.startsWith('data:image/')) {
    return 'unknown'
  }
  const mimeType = base64Data.match(/^data:(image\/[a-z]+);base64,/)[1]
  switch (mimeType) {
    case 'image/png':
      return 'png'
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpg'
    default:
      return mimeType.replace('image/', '')
  }
}

function base64ToBlob(base64Data) {
  try {
    const matches = base64Data.match(/^data:(.+);base64,(.+)$/)
    if (!matches || matches.length !== 3) {
      throw new Error('Invalid base64 data.')
    }
    const mimeType = matches[1]
    const base64String = matches[2]
    const binaryString = atob(base64String)
    const bytes = new Uint8Array(binaryString.length)

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return new Blob([bytes], { type: mimeType })
  } catch (err) {
    throw new Error(`Base64 convert to Blob failed. ${err.message}`)
  }
}

export {
  type imageSize,
  showConfirm,
  showAlert,
  showMessage,
  formatTime,
  isEmpty,
  getImageSize,
  download,
}
